import React, { useRef, useEffect } from 'react';

const Particles = ({ className = '', config = {} }) => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const mouse = useRef({ x: 0, y: 0, clicked: false });

  // Default configuration - optimized for performance
  const defaultConfig = {
    color: '#FFFFFF',
    density: 0.3, // Reduced from 0.5
    lineLinked: true,
    moveSpeed: 2, // Reduced from 3
    size: 2,
    opacity: 0.5, // Reduced from 0.7
    ...config,
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Particle class
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = defaultConfig.size;
        this.baseSize = defaultConfig.size;
        this.opacity = defaultConfig.opacity;
        this.speedX = Math.random() * defaultConfig.moveSpeed - defaultConfig.moveSpeed / 2;
        this.speedY = Math.random() * defaultConfig.moveSpeed - defaultConfig.moveSpeed / 2;
        this.color = defaultConfig.color;
      }

      update() {
        // Mouse interaction - simplified
        const dx = mouse.current.x - this.x;
        const dy = mouse.current.y - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 80) { // Reduced interaction radius from 100 to 80
          this.size = Math.min(this.baseSize * 2, this.size + 0.05); // Reduced multiplier
          const force = (80 - distance) / 80;
          this.speedX += dx * force * 0.005; // Reduced force
          this.speedY += dy * force * 0.005;
        } else if (this.size > this.baseSize) {
          this.size -= 0.05;
        }

        // Movement
        this.x += this.speedX;
        this.y += this.speedY;

        // Bounce edges
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }

      draw() {
        ctx.fillStyle = `rgba(${hexToRgb(this.color)}, ${this.opacity})`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Helper functions
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result 
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '255, 255, 255';
    };

    // Initialize particles - reduced count
    const initParticles = () => {
      particles.current = Array(Math.floor((canvas.width * canvas.height * defaultConfig.density) / 15000)) // Increased divisor for fewer particles
        .fill(null)
        .map(() => new Particle());
    };

    // Animation loop
    let animationFrame;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.current.forEach(particle => {
        particle.update();
        particle.draw();
        
        // Draw lines between close particles
        if (defaultConfig.lineLinked) {
          particles.current.forEach(other => {
            const dx = particle.x - other.x;
            const dy = particle.y - other.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 100) {
              ctx.beginPath();
              ctx.strokeStyle = `rgba(${hexToRgb(defaultConfig.color)}, ${1 - distance/100})`;
              ctx.lineWidth = 0.5;
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(other.x, other.y);
              ctx.stroke();
            }
          });
        }
      });

      animationFrame = requestAnimationFrame(animate);
    };

    // Mouse handlers
    const handleMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };

    const handleMouseClick = () => {
      mouse.current.clicked = true;
      setTimeout(() => {
        mouse.current.clicked = false;
      }, 100);
    };

    // Initialize
    initParticles();
    animate();
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleMouseClick);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleMouseClick);
      cancelAnimationFrame(animationFrame);
    };
  }, [defaultConfig]);

  return (
    <div className={`absolute inset-0 ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full"
      />
    </div>
  );
};

export default Particles;